import { cx } from '@linaria/core';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
import { getCoWorkersQuery, getClassesQuery } from '@pm2/api';
import { useCourseBuilder, getAllCoursesQuery, courseBuilderStore, CourseBuilderPanel } from '@pm2/course-builder';
import { useLeaveRevision, useCurrentCourseDescriptor } from '@pm2/course-revisions';
import { pm2ConfigStore } from '@pm2/store';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { Tooltip, CoursesIcon } from '@ui/kit';
import { useMatchBreakpoint, IsomorphicPortal } from '@utils/react';
import { guideBubbleWrapperCls, toolButtonCls } from './root.tools.style.js';
import { button } from './tool.course-builder.style.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Aside } from '../../aside/Aside.js';

function CourseBuilder({
  enabled,
  onEnable,
  onDisable
}) {
  const user = useUserIdentityState(state => state.user);
  const isHidden = useMatchBreakpoint(breakpoints.tablet.andDown());
  const queryClient = useQueryClient();
  const showCourseBuilderPanel = useCourseBuilder(state => state.showPanel);
  const showCoursePlanner = pm2ConfigStore.useSelector(state => state.config.settings.showCoursePlanner);
  const loginDisabled = pm2ConfigStore.useSelector(state => state.config.settings.loginDisabled);
  const closePanel = () => {
    courseBuilderStore.dispatch(courseBuilderStore.actions.togglePanel(false));
  };
  const courses = useQuery({
    ...getAllCoursesQuery(user),
    enabled: showCourseBuilderPanel
  });
  const coworkers = useQuery({
    ...getCoWorkersQuery(user),
    enabled: showCourseBuilderPanel
  });
  const classes = useQuery({
    ...getClassesQuery(user),
    enabled: showCourseBuilderPanel
  });
  const leaveRevision = useLeaveRevision(useCurrentCourseDescriptor());

  // Load relevant courses as soon as the CourseBuilder panel is requsted
  useEffect(() => {
    if (enabled) {
      queryClient.prefetchQuery(getAllCoursesQuery(user));
      queryClient.prefetchQuery(getCoWorkersQuery(user));
      queryClient.prefetchQuery(getClassesQuery(user));
    }
  }, [enabled, queryClient, user]);

  // trigger onEnable / onDisable in response to state changes
  useEffect(() => {
    if (showCourseBuilderPanel) {
      onEnable();
    } else {
      onDisable();
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [showCourseBuilderPanel]);

  // register when aside is collapsed due to outside changes, and update
  // internal state properly
  useEffect(() => {
    courseBuilderStore.dispatch(courseBuilderStore.actions.togglePanel(enabled));
  }, [enabled]);

  // hide the course builder on devices smaller than 1024px
  useEffect(() => {
    if (isHidden) {
      courseBuilderStore.dispatch(courseBuilderStore.actions.togglePanel(false));
    }
  }, [isHidden]);

  // enable the course picker when user clicks this tool button (if it's
  // available on the portal)
  const handleClick = useCallback(async evt => {
    // we need to avoid auto-collapsing SubNavigation when opening the
    // CourseBuilder (as these are explicitly allowed to be opened at the
    // same time)
    //
    // as the SubNavigation uses a clickOutsideListener to auto-collapse, we
    // stop event propagation to avoid that listener from being triggered
    //
    // this is considered to be safe, as right-aligned asides will be auto
    // collapsed when opening the CourseBuilder in another right-aligned
    // aside anyway, and top-level tools will auto-collapse upon toggling of
    // the course builder as well, furthermore lightboxes will make it
    // impossible to interact with this button as well
    evt.stopPropagation();
    const intent = await leaveRevision();
    if (intent === "cancel") {
      return;
    }
    courseBuilderStore.dispatch(courseBuilderStore.actions.togglePanel());
  }, [leaveRevision]);

  // never render if the tool wasn't explicitly enabled
  if (!showCoursePlanner) {
    return null;
  }

  // only render the tool for teachers, unless login is currently disabled
  // (in which case we want to indicate it's presence, but not actually make
  // it available)
  if (!loginDisabled && (user === undefined || user?.role !== "teacher")) {
    return null;
  }
  const label = !loginDisabled ? "Sammensæt forløb" : "Denne funktion kan ikke tilgås i øjeblikket, da vi har problemer med login.";
  const isLoading = enabled && (courses.isLoading || coworkers.isLoading || classes.isLoading);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isHidden && /*#__PURE__*/jsx("div", {
      className: guideBubbleWrapperCls,
      children: /*#__PURE__*/jsx(Tooltip, {
        placement: "bottom",
        body: label,
        ariaType: loginDisabled ? "description" : "label",
        offsetPx: 0,
        children: ref => /*#__PURE__*/jsx("button", {
          ref: ref,
          className: cx(toolButtonCls, button, loginDisabled && "disabled", isLoading && "pulsating"),
          "aria-label": label,
          onClick: handleClick,
          children: /*#__PURE__*/jsx(CoursesIcon, {
            style: {
              height: "26px",
              marginBottom: "-5px"
            }
          })
        })
      })
    }), /*#__PURE__*/jsx(IsomorphicPortal, {
      children: /*#__PURE__*/jsx(Aside, {
        preventOverlap: true,
        align: "right",
        expanded: showCourseBuilderPanel && !courses.isPending,
        onClose: closePanel,
        children: /*#__PURE__*/jsx(CourseBuilderPanel, {})
      })
    })]
  });
}

export { CourseBuilder };
//# sourceMappingURL=tool.course-builder.js.map
